/**
 * Premium Instagram Search Field Component
 *
 * Features:
 * - Responsive 300ms debounced search
 * - Skeleton loaders during search
 * - Smooth avatar loading with shimmer effect
 * - Graceful error handling and fallbacks
 * - Keyboard navigation support
 * - Premium animations and transitions
 *
 * Design inspired by Linear, Stripe, and Airbnb patterns.
 */

import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import useSWR from 'swr';
import { Combobox, Transition } from '@headlessui/react';
import type { IGQueryTypes } from '@shared-libs/instagram';
import { decodeInstagramMediaURL } from '@shared-libs/instagram/media-url';
import { MdVerified } from 'react-icons/md';
import AvatarWithSkeleton, { AvatarSkeleton } from './AvatarWithSkeleton';
import {
  MIN_SEARCH_CHARS,
  SEARCH_DEBOUNCE_MS,
  normalizeSearchQuery,
  searchCacheKey,
  createSearchCache,
} from '../../../utils/search-throttle';

// Per-session memo (query -> Instagram results). Module scope so retyping a
// query already fetched this session serves from here with NO network /
// StarAPI call.
const igSearchCache = createSearchCache<unknown[]>();

// Types
export interface InstagramAccount {
  user_id: string;
  username: string;
  full_name?: string;
  profile_pic_url?: string;
  is_verified?: boolean;
  is_private?: boolean;
}

export interface InstagramHashtag {
  target_id: string;
  name: string;
  mediaCount: number;
  search_result_subtitle: string;
}

type InstagramSearchResult = InstagramAccount | InstagramHashtag;

interface InstagramSearchFieldProps {
  onResultSelected?: (result: InstagramSearchResult) => void;
  onValueChange?: (value: string) => void;
  onEnterKeyPressed?: () => void;
  disabled?: boolean;
  autocompleteOnSelect?: boolean;
  searchType?: 'users' | 'hashtags';
  autoFocus?: boolean;
  disableSearch?: boolean;
  defaultValue?: string;
}

// Debounce window before a settled query is searched (StarAPI-cost throttle).
const DEBOUNCE_MS = SEARCH_DEBOUNCE_MS;
// A 1-2 char query is almost never a real target and returns noise, so the
// minimum before any network search is MIN_SEARCH_CHARS (3).
const MIN_SEARCH_LENGTH = MIN_SEARCH_CHARS;

// Custom debounce hook for cleaner implementation
function useDebounce<T>(value: T, delay: number): T {
  const [debouncedValue, setDebouncedValue] = useState<T>(value);

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedValue(value);
    }, delay);

    return () => clearTimeout(handler);
  }, [value, delay]);

  return debouncedValue;
}

// Loading spinner component - wrapped in container to prevent transform conflict
// (animate-spin uses transform:rotate which conflicts with -translate-y-1/2)
function LoadingSpinner() {
  return (
    <div className="absolute top-1/2 right-4 -translate-y-1/2 flex items-center justify-center">
      <svg
        className="h-5 w-5 animate-spin text-[#7549f4]"
        xmlns="http://www.w3.org/2000/svg"
        fill="none"
        viewBox="0 0 24 24"
      >
        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="3" />
        <path
          className="opacity-75"
          fill="currentColor"
          d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
        />
      </svg>
    </div>
  );
}

// Skeleton loader for search results
function SearchResultSkeleton() {
  return (
    <div className="flex items-center w-full px-3 py-2.5 space-x-3">
      <AvatarSkeleton size="md" />
      <div className="flex-1 space-y-2">
        <div className="h-4 w-24 bg-gray-200 rounded animate-pulse" />
        <div className="h-3 w-32 bg-gray-100 rounded animate-pulse" />
      </div>
    </div>
  );
}

// Instagram icon for left side of input
function InstagramIcon({ className = '' }: { className?: string }) {
  return (
    <svg className={className} xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 20 20" fill="none">
      <path d="M10.0008 7.50002C8.61966 7.50002 7.50081 8.61927 7.50081 10C7.50081 11.3811 8.62008 12.5 10.0008 12.5C11.3819 12.5 12.5008 11.3808 12.5008 10C12.5008 8.61894 11.3816 7.50002 10.0008 7.50002ZM10.0008 5.83335C12.3012 5.83335 14.1675 7.6976 14.1675 10C14.1675 12.3004 12.3032 14.1667 10.0008 14.1667C7.70042 14.1667 5.83415 12.3024 5.83415 10C5.83415 7.69963 7.6984 5.83335 10.0008 5.83335ZM15.4175 5.62431C15.4175 6.1994 14.9502 6.666 14.3758 6.666C13.8007 6.666 13.3342 6.19869 13.3342 5.62431C13.3342 5.04994 13.8014 4.58335 14.3758 4.58335C14.9494 4.58263 15.4175 5.04994 15.4175 5.62431ZM10.0008 3.33335C7.93875 3.33335 7.60265 3.33881 6.64356 3.38152C5.99012 3.4122 5.55211 3.50008 5.14513 3.65808C4.78346 3.79835 4.52257 3.96584 4.2446 4.24381C3.96556 4.52285 3.79836 4.78305 3.65866 5.14488C3.5003 5.55279 3.41244 5.99011 3.38231 6.64265C3.33919 7.56269 3.33415 7.88423 3.33415 10C3.33415 12.0621 3.33961 12.3982 3.38231 13.3572C3.413 14.0104 3.501 14.449 3.65861 14.855C3.79925 15.2172 3.96708 15.4787 4.24371 15.7554C4.52386 16.0351 4.78482 16.2029 5.14322 16.3412C5.55516 16.5004 5.9929 16.5884 6.64343 16.6185C7.56348 16.6616 7.88502 16.6667 10.0008 16.6667C12.0629 16.6667 12.399 16.6612 13.358 16.6185C14.0097 16.5879 14.4487 16.4997 14.8558 16.3422C15.217 16.2019 15.4793 16.0335 15.7562 15.7571C16.0363 15.4765 16.2037 15.2162 16.3422 14.8569C16.5011 14.4465 16.5892 14.0082 16.6193 13.3574C16.6624 12.4374 16.6675 12.1158 16.6675 10C16.6675 7.93795 16.662 7.60186 16.6193 6.64283C16.5887 5.9909 16.5004 5.55126 16.3427 5.14434C16.2028 4.78367 16.0347 4.52198 15.757 4.24381C15.4775 3.96431 15.218 3.79746 14.8559 3.65787C14.4483 3.49964 14.0103 3.41165 13.3582 3.38153C12.4382 3.3384 12.1166 3.33335 10.0008 3.33335ZM10.0008 1.66669C12.2647 1.66669 12.5473 1.67502 13.4362 1.71669C14.323 1.75766 14.9279 1.89794 15.4592 2.10419C16.0084 2.316 16.4723 2.6021 16.9355 3.0653C17.398 3.5285 17.6842 3.99377 17.8967 4.54169C18.1022 5.07225 18.2425 5.6778 18.2842 6.5646C18.3237 7.4535 18.3342 7.73613 18.3342 10C18.3342 12.2639 18.3258 12.5465 18.2842 13.4354C18.2432 14.3223 18.1022 14.9271 17.8967 15.4584C17.6848 16.0077 17.398 16.4715 16.9355 16.9348C16.4723 17.3973 16.0063 17.6834 15.4592 17.8959C14.9279 18.1014 14.323 18.2417 13.4362 18.2834C12.5473 18.3229 12.2647 18.3334 10.0008 18.3334C7.73692 18.3334 7.45428 18.325 6.5654 18.2834C5.67859 18.2424 5.07442 18.1014 4.54248 17.8959C3.99386 17.684 3.52928 17.3973 3.06609 16.9348C2.6029 16.4715 2.31748 16.0056 2.10498 15.4584C1.89873 14.9271 1.75915 14.3223 1.71748 13.4354C1.6779 12.5465 1.66748 12.2639 1.66748 10C1.66748 7.73613 1.67581 7.4535 1.71748 6.5646C1.75845 5.6771 1.89873 5.07294 2.10498 4.54169C2.31678 3.99308 2.6029 3.5285 3.06609 3.0653C3.52928 2.6021 3.99456 2.31669 4.54248 2.10419C5.07373 1.89794 5.6779 1.75835 6.5654 1.71669C7.45428 1.6771 7.73692 1.66669 10.0008 1.66669Z" fill="#BBB9C0" />
    </svg>
  );
}

// Hashtag icon
function HashtagIcon({ className = '' }: { className?: string }) {
  return (
    <svg className={className} xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 20 20" fill="none">
      <path d="M7.91682 2.5L5.41682 17.5M14.5835 2.5L12.0835 17.5M17.0835 6.66667H2.91683M16.2502 13.3333H2.0835" stroke="#BBB9C0" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

// Gradient hashtag icon for results
function GradientHashtagIcon() {
  return (
    <div className="w-10 h-10 rounded-full bg-gradient-to-br from-[#7549F4]/10 to-[#FCA6E9]/10 flex items-center justify-center">
      <svg className="w-5 h-5" xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16" fill="none">
        <path d="M6.33316 2L4.33316 14M11.6665 2L9.66648 14M13.6665 5.33333H2.33317M12.9998 10.6667H1.6665" stroke="url(#paint0_linear_hashtag)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
        <defs>
          <linearGradient id="paint0_linear_hashtag" x1="-6.51811" y1="13.3231" x2="25.2358" y2="4.46186" gradientUnits="userSpaceOnUse">
            <stop stopColor="#7549F4" />
            <stop offset="1" stopColor="#FCA6E9" />
          </linearGradient>
        </defs>
      </svg>
    </div>
  );
}

// Add button icon
function AddIcon() {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 20 20" fill="none">
      <g clipPath="url(#clip0_add)">
        <path d="M9.99984 6.6665V13.3332M6.6665 9.99984H13.3332M18.3332 9.99984C18.3332 14.6022 14.6022 18.3332 9.99984 18.3332C5.39746 18.3332 1.6665 14.6022 1.6665 9.99984C1.6665 5.39746 5.39746 1.6665 9.99984 1.6665C14.6022 1.6665 18.3332 5.39746 18.3332 9.99984Z" stroke="#BBB9C0" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
      </g>
      <defs>
        <clipPath id="clip0_add">
          <rect width="20" height="20" fill="white" />
        </clipPath>
      </defs>
    </svg>
  );
}

export default function InstagramSearchField({
  onResultSelected = () => {},
  onValueChange = () => {},
  onEnterKeyPressed = () => {},
  disabled = false,
  autocompleteOnSelect = true,
  searchType = 'users',
  autoFocus = true,
  disableSearch = false,
  defaultValue = '',
}: InstagramSearchFieldProps) {
  const [results, setResults] = useState<InstagramSearchResult[]>([]);
  const [selectedResult, setSelectedResult] = useState<InstagramSearchResult | undefined>(undefined);
  const [inputValue, setInputValue] = useState(defaultValue);
  const [isTyping, setIsTyping] = useState(false);

  const inputRef = useRef<HTMLInputElement>(null);
  const debouncedQuery = useDebounce(inputValue, DEBOUNCE_MS);

  // Cancel the previous in-flight search on each new one, and guard against a
  // late/aborted response from an earlier keystroke overwriting newer results.
  const abortRef = useRef<AbortController | null>(null);
  const requestIdRef = useRef(0);

  // Normalized query (trim, '@' strip, min length, valid chars). Null == no
  // fetch: too short, whitespace-only, or invalid handle characters.
  const searchQuery = normalizeSearchQuery(debouncedQuery, MIN_SEARCH_LENGTH);

  // Fetch function for SWR
  const fetchResults = useCallback(async (query: string) => {
    // Cancel any earlier in-flight request before starting a new one.
    abortRef.current?.abort();

    // Session memo: a query already fetched this session never refetches.
    const cacheKey = searchCacheKey(searchType, query);
    if (igSearchCache.has(cacheKey)) {
      return igSearchCache.get(cacheKey) as InstagramSearchResult[];
    }

    const controller = new AbortController();
    abortRef.current = controller;
    const requestId = ++requestIdRef.current;

    const res = await fetch(
      `/api/instagram/search-targets?type=${searchType as IGQueryTypes}&search=${encodeURIComponent(query)}`,
      { signal: controller.signal },
    );
    const targets = await res.json();
    // Drop a stale response — a newer keystroke already superseded this one.
    if (requestId !== requestIdRef.current) return targets;
    const normalized = Array.isArray(targets) ? targets : [];
    igSearchCache.set(cacheKey, normalized);
    return normalized;
  }, [searchType]);

  // SWR for data fetching with proper key
  const { data, error, isValidating } = useSWR(
    searchQuery ? searchCacheKey(searchType, searchQuery) : null,
    () => fetchResults(searchQuery as string),
    {
      revalidateOnFocus: false,
      dedupingInterval: 2000,
      // Aborted requests reject with AbortError — never retry them (each retry
      // is a paid StarAPI call). Genuine errors surface via the empty state.
      errorRetryCount: 0,
      keepPreviousData: true,
      onError: (err) => {
        if ((err as { name?: string })?.name === 'AbortError') return;
      },
    }
  );

  // Update results when data changes
  useEffect(() => {
    if (Array.isArray(data)) {
      setResults(data);
    }
  }, [data]);

  // Calculate loading states
  const isSearching = isValidating || (isTyping && inputValue.length >= MIN_SEARCH_LENGTH);
  const showSkeleton = isSearching && results.length === 0;
  const hasResults = results.length > 0 && inputValue.length >= MIN_SEARCH_LENGTH;

  // Focus on mount if autoFocus
  useEffect(() => {
    if (inputRef.current && autoFocus && !inputRef.current.value) {
      inputRef.current.focus();
    }
  }, [autoFocus]);

  // Handle default value
  useEffect(() => {
    if (inputRef.current && defaultValue) {
      inputRef.current.value = defaultValue;
      setInputValue(defaultValue);
    }
  }, [defaultValue]);

  // Compare function for Combobox
  const compareResults = useCallback((a: InstagramSearchResult, b: InstagramSearchResult): boolean => {
    if (!a || !b) return false;
    if ('username' in a && 'username' in b) return a.username.toLowerCase() === b.username.toLowerCase();
    if ('name' in a && 'name' in b) return a.name.toLowerCase() === b.name.toLowerCase();
    return false;
  }, []);

  // Handle input change
  const handleInputChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setInputValue(value);
    setIsTyping(true);
    onValueChange(value);

    // Clear typing state after debounce
    setTimeout(() => setIsTyping(false), DEBOUNCE_MS + 50);
  }, [onValueChange]);

  // Handle result selection
  const handleResultSelect = useCallback((result: InstagramSearchResult) => {
    setSelectedResult(result);
    onResultSelected(result);

    const displayValue = 'username' in result ? result.username : result.name;
    onValueChange(displayValue);

    if (autocompleteOnSelect) {
      setInputValue(displayValue);
      if (inputRef.current) inputRef.current.value = displayValue;
    } else {
      setInputValue('');
      if (inputRef.current) inputRef.current.value = '';
    }

    // Clear results after selection
    setResults([]);
  }, [autocompleteOnSelect, onResultSelected, onValueChange]);

  // Handle keyboard events
  const handleKeyDown = useCallback((e: React.KeyboardEvent<HTMLInputElement>) => {
    // Handle Tab
    if (e.key === 'Tab') {
      e.preventDefault();
      const focusable = Array.from(
        document.querySelectorAll('button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])')
      );
      const currentIndex = focusable.indexOf(document.activeElement as Element);
      const nextElement = focusable[(currentIndex + 1) % focusable.length] as HTMLElement;
      nextElement?.focus();
    }

    // Handle Enter
    if (e.key === 'Enter') {
      e.preventDefault();
      onEnterKeyPressed();
    }

    // Only allow valid Instagram characters
    const regex = /^[a-zA-Z0-9_.]$/;
    if (e.key.length === 1 && !regex.test(e.key)) {
      e.preventDefault();
    }
  }, [onEnterKeyPressed]);

  // Handle paste
  const handlePaste = useCallback((e: React.ClipboardEvent<HTMLInputElement>) => {
    e.preventDefault();
    const text = e.clipboardData.getData('text');
    const regex = /^[a-zA-Z0-9_.]*$/;
    const filteredText = text.split('').filter(char => regex.test(char)).join('');

    if (e.target instanceof HTMLInputElement) {
      e.target.value = filteredText;
      setInputValue(filteredText);
      onValueChange(filteredText);
    }
  }, [onValueChange]);

  // Get avatar URL for selected result
  const selectedAvatarUrl = useMemo(() => {
    if (selectedResult && 'profile_pic_url' in selectedResult && inputValue === selectedResult.username) {
      return selectedResult.profile_pic_url
        ? decodeInstagramMediaURL(selectedResult.profile_pic_url)
        : null;
    }
    return null;
  }, [selectedResult, inputValue]);

  return (
    <Combobox
      value={selectedResult}
      by={compareResults}
      disabled={disabled}
    >
      <div className="relative">
        {/* Left icon */}
        <div className="absolute left-4 top-1/2 -translate-y-1/2 z-10">
          {searchType === 'users' ? (
            selectedAvatarUrl ? (
              <AvatarWithSkeleton
                src={selectedAvatarUrl}
                alt={selectedResult && 'username' in selectedResult ? selectedResult.username : 'User'}
                size="xs"
                className="w-5 h-5"
              />
            ) : (
              <InstagramIcon className="w-5 h-5" />
            )
          ) : (
            <HashtagIcon className="w-5 h-5" />
          )}
        </div>

        {/* Input */}
        <Combobox.Button as="div" className="flex">
          <Combobox.Input
            ref={inputRef}
            placeholder={searchType === 'users' ? 'therock' : 'fitness'}
            onChange={handleInputChange}
            onKeyDown={handleKeyDown}
            onPaste={handlePaste}
            displayValue={(result: InstagramSearchResult) =>
              result ? ('username' in result ? result.username : result.name) : ''
            }
            className={`
              block w-full rounded-[10px] py-2.5 px-4 pl-12 pr-10 text-[1rem]
              bg-white ring-1 ring-gray-200 border-0
              placeholder:text-gray-400 text-gray-900
              transition-all duration-200 ease-out
              focus:ring-2 focus:ring-[#7549f4]/20 focus:border-[#7549f4]
              focus:shadow-[0_0_0_3px_rgba(117,73,244,0.1)]
              disabled:cursor-not-allowed disabled:opacity-50 disabled:bg-gray-50
              ${disabled ? 'bg-gray-50' : ''}
            `}
            required={autoFocus}
            readOnly={disabled}
            autoComplete="off"
            autoCorrect="off"
            autoCapitalize="off"
            spellCheck={false}
            maxLength={50}
            minLength={MIN_SEARCH_LENGTH}
            autoFocus={autoFocus}
          />
        </Combobox.Button>

        {/* Loading spinner */}
        {isSearching && <LoadingSpinner />}
      </div>

      {/* Dropdown */}
      <Transition as="div"
        className="relative z-50"
        enter="transition duration-150 ease-out"
        enterFrom="opacity-0 translate-y-1"
        enterTo="opacity-100 translate-y-0"
        leave="transition duration-100 ease-in"
        leaveFrom="opacity-100 translate-y-0"
        leaveTo="opacity-0 translate-y-1"
      >
        <Combobox.Options as="ul"
          className={`
            absolute left-0 top-2 w-full max-h-[280px] overflow-auto
            rounded-xl bg-white border border-gray-200
            shadow-[0_4px_24px_-4px_rgba(0,0,0,0.12),0_0_1px_rgba(0,0,0,0.1)]
            py-2 z-50
            ${hasResults || showSkeleton ? '' : 'hidden'}
          `}
        >
          {/* Skeleton loading state */}
          {showSkeleton && (
            <>
              <SearchResultSkeleton />
              <SearchResultSkeleton />
              <SearchResultSkeleton />
            </>
          )}

          {/* Results */}
          {hasResults && results.map((result, index) => (
            <Combobox.Option as="li"
              key={'username' in result ? result.username : result.name}
              value={result}
              className="px-2 py-0.5"
              onClick={() => handleResultSelect(result)}
            >
              {({ active }) => (
                <div
                  className={`
                    flex items-center w-full px-3 py-2.5 space-x-3 rounded-lg cursor-pointer
                    transition-colors duration-100 ease-out
                    ${active ? 'bg-gray-50' : ''}
                  `}
                >
                  {/* Avatar or Hashtag icon */}
                  {'username' in result ? (
                    <AvatarWithSkeleton
                      src={result.profile_pic_url ? decodeInstagramMediaURL(result.profile_pic_url) : null}
                      alt={result.username}
                      fallbackInitials={result.username.slice(0, 2).toUpperCase()}
                      size="md"
                      showVerifiedBadge={result.is_verified}
                    />
                  ) : (
                    <GradientHashtagIcon />
                  )}

                  {/* Text content */}
                  <div className="flex-1 min-w-0">
                    <p className="flex items-center font-medium text-gray-900 truncate">
                      {'username' in result ? result.username : `#${result.name}`}
                      {'is_verified' in result && result.is_verified && (
                        <MdVerified className="ml-1 text-[#00a0ff] flex-shrink-0" />
                      )}
                    </p>
                    {'full_name' in result && result.full_name && (
                      <p className="text-sm text-gray-500 truncate">{result.full_name}</p>
                    )}
                    {'mediaCount' in result && (
                      <p className="text-sm text-gray-500">{result.mediaCount?.toLocaleString()} posts</p>
                    )}
                  </div>

                  {/* Add button */}
                  <div className={`flex-shrink-0 transition-opacity duration-150 ${active ? 'opacity-100' : 'opacity-50'}`}>
                    <AddIcon />
                  </div>
                </div>
              )}
            </Combobox.Option>
          ))}

          {/* Empty state */}
          {!showSkeleton && !hasResults && inputValue.length >= MIN_SEARCH_LENGTH && !isSearching && (
            <div className="px-4 py-8 text-center">
              <p className="text-sm font-medium text-gray-900">No results found</p>
              <p className="mt-1 text-xs text-gray-500">
                Try searching with a different {searchType === 'users' ? 'username' : 'hashtag'}
              </p>
            </div>
          )}
        </Combobox.Options>
      </Transition>
    </Combobox>
  );
}
